import React, { Component } from "react";
import PropTypes from "prop-types";
import Bugsnag from "@bugsnag/js";
import ErrorFallback from "./ErrorFallback";

/**
 * ErrorBoundary component catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to Bugsnag
    Bugsnag.notify(error, event => {
      event.addMetadata("errorInfo", errorInfo);
    });

    // Update state with error details
    this.setState({ errorInfo });

    // You can also log the error to other error reporting services
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === "development") console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // You can render any custom fallback UI
      if (fallback) {
        return fallback(error, errorInfo?.componentStack, this.resetError);
      }

      return <ErrorFallback error={error} componentStack={errorInfo?.componentStack} resetError={this.resetError} />;
    }

    return children;
  }
}

// noinspection JSDeprecatedSymbols
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
};

export default ErrorBoundary;
